import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ArrowUpRightIcon } from 'lucide-react';
import { Logo } from '../components/Logo';
import { creditPartners, CreditPartner } from '../data/credits';
type CreditsProps = {
  slug: CreditPartner['slug'];
};
export function Credits({
  slug
}: CreditsProps) {
  const partner = creditPartners.find(p => p.slug === slug) ?? creditPartners[0];
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Credits — ${partner.name} — Superluxury`;
  }, [partner]);
  return <>
      <header className="sticky top-0 z-40 border-b border-line bg-paper" data-id="element-1480" data-mp="2kb8fc8f3jrss">
        <div className="mx-auto flex h-16 max-w-[1320px] items-center justify-between px-6 md:h-20 md:px-16" data-id="element-1481" data-mp="2k9fa8xk4tfmw">
          <Link to="/" className="inline-flex items-center gap-2 text-[14px] font-light text-ink/70 transition-colors duration-150 hover:text-ink" data-id="element-1482" data-mp="2gbzmr6vslzyt">
            <ArrowLeftIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-1483" data-mp="14t6oky6np4xu" />
            <span className="hidden sm:inline" data-id="element-1484" data-mp="x9doj66o285u">Back to Superluxury</span>
          </Link>
          <Link to="/" aria-label="Superluxury — home" data-id="element-1485" data-mp="126m8ncbdkm9q">
            <Logo className="text-[16px] md:text-[20px]" data-id="element-1486" data-mp="o8ftvei4bczx" />
          </Link>
          <span className="w-4 sm:w-[150px]" aria-hidden="true" data-id="element-1487" data-mp="oa8p7xna6asm" />
        </div>
      </header>

      {/* Credits stay in English, like the rest of the legal pages */}
      <main translate="no" className="bg-paper px-6 pb-28 pt-16 md:px-16 md:pb-36 md:pt-28" data-id="element-1488" data-mp="ehkur9jrwix2">
        <div className="mx-auto max-w-[1320px]" data-id="element-1489" data-mp="ja4gwuwj0mdh">
          <p className="text-[14px] font-light text-ink/60" data-id="element-1490" data-mp="jaw1laol4zoz">Credits</p>
          {partner.logo ? <h1 className="mt-6 md:mt-8" data-id="element-1491" data-mp="9j9k4wlnm4ds">
              <img src={partner.logo} alt={partner.name} className="block h-[52px] w-auto md:h-[84px]" data-id="element-1492" data-mp="9kb6vyc18fbc" />
            </h1> : <h1 className="mt-4 font-editorial text-[52px] leading-[54px] tracking-[-0.01em] md:text-[88px] md:leading-[88px]" data-id="element-1493" data-mp="hjv1xf8vhj6">
              {partner.name}
            </h1>}
          {partner.description && <p className="mt-5 max-w-[520px] text-[19px] font-light leading-[30px] text-ink/75" data-id="element-1494" data-mp="1xfhcxgt80e0z">{partner.description}</p>}

          <div className="mt-16 grid grid-cols-1 gap-12 border-t border-line pt-12 md:mt-20 lg:grid-cols-12 lg:gap-16" data-id="element-1495" data-mp="1ogcj2nslt8v5">
            <div className="lg:col-span-7" data-id="element-1496" data-mp="2l74xzi3swuvc">
              <p className="text-[22px] font-light leading-[34px] md:text-[28px] md:leading-[40px]" data-id="element-1497" data-mp="2l5twxq1qq17x">{partner.statement}</p>
              {partner.note && <p className="mt-6 text-[16px] font-light text-ink/60" data-id="element-1498" data-mp="2c5onlaczksr4">{partner.note}</p>}
              <a href={partner.link.href} target="_blank" rel="noopener noreferrer" className="group mt-10 inline-flex items-center gap-2 border-b border-ink pb-1 text-[17px] font-normal" data-id="element-1499" data-mp="276u8aie89mbr">
                {partner.link.label}
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-1500" data-mp="37f0kd6egp98g" />
              </a>
            </div>

            <div className="lg:col-span-4 lg:col-start-9" data-id="element-1501" data-mp="3l6oy1e4d05rj">
              <h2 className="text-[14px] font-light text-ink/60" data-id="element-1502" data-mp="3l89uxae5c0sk">Scope of work</h2>
              <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-1503" data-mp="3q59wp93yqhdv">
                {partner.roles.map(role => <li key={role} className="py-3.5 text-[17px] font-light" data-id="element-1504" data-mp="3ybp4gkbxly3">
                    {role}
                  </li>)}
              </ul>
              <p className="mt-6 text-[15px] font-light leading-[24px] text-ink/60" data-id="element-1505" data-mp="7z413hkl64y2">
                Client: KAYS VENTURES FZCO · Superluxury · superluxury.io
              </p>
            </div>
          </div>
        </div>
      </main>
    </>;
}